/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';

export function getCodeBlocks(feature: BaseNode): CodeBlock[] {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const TABLE = project.getFeature(feature.getSettingValue('TABLE'));
    if (!TABLE) {
        return blocks;
    }
    const TABLE_NAME = TABLE.getSettingValue('NAME');
    const FIELDS = TABLE.getSettingValue('FIELDS');


    const FIELD_NAME = feature.getSettingValue('FIELD_NAME');
    const OPERATOR = feature.getSettingValue('OPERATOR');
    const findFunctionName = `${TABLE_NAME}_found_${feature.getID()}`;
    let links;

    links = project.getNodeLinks(feature, 'tbl_record_not_found');
    let notFound = '';
    if (links.length > 0) {
        const link = links[0];
        notFound += `'****${link.getSourceId()}****'`;
    }

    links = project.getNodeLinks(feature, 'tbl_record_find');

    const params = [];
    for (let i = 0; i < FIELDS.length; i += 1) {
        const field = FIELDS[i];
        let dataType;
        const FIELDTYPE = field.getSettingValue('FIELDTYPE');
        switch (FIELDTYPE) {
            case 'M':
            case 'E':
                dataType = TibboDataType.DWORD;
                break;
            case 'S':
                dataType = TibboDataType.STRING;
                break;
            default:
                dataType = TibboDataType.FLOAT;
                break;
        }
        const param = `byref ${field.getSettingValue('NAME')} as ${dataType}`;
        params.push(param);
    }

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        id: `declare_${findFunctionName}`,
        blockType: BlockType.CODE,
        template: `
'Query found result for ${TABLE_NAME}
declare sub ${findFunctionName}(row_index as word,${params.join(',')})`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        id: 'declare_table_find_result_index',
        template: `
declare table_find_result_index as word
declare tmp_table_find_result_index as word`,
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        id: 'dims_table_find_result_index',
        template: `
dim tmp_table_find_result_index as word            
dim table_find_result_index as word`,
    });

    for (let i = 0; i < links.length; i += 1) {
        const link = links[i];
        let find_index = project.getNodeParameter(link, 'find_index');
        if (find_index === '') {
            find_index = 0;
        }
        let fieldValue = project.getNodeParameter(link, 'field_value');
        for (let i = 0; i < FIELDS.length; i += 1) {
            if (FIELDS[i].getSettingValue('NAME') === FIELD_NAME) {
                const field = FIELDS[i];
                const fieldDataType = field.getSettingValue('FIELDTYPE');
                const source: BaseNode = project.getFeature(link.getSettingValue('SOURCE_FEATURE')) as BaseNode;
                const sourceParams = source.getPort(link.getSettingValue('SOURCE_PORT'))?.params;
                switch (fieldDataType) {
                    case 'E': // Timestamp
                        fieldValue = `lstr(${project.parseCodeExpression(project.getNodeParameterValue(feature, 'field_value'), sourceParams, TibboDataType.DATETIME)})`;
                        break;
                    case 'M': // Time in seconds
                        fieldValue = `fstr(${project.parseCodeExpression(project.getNodeParameterValue(feature, 'field_value'), sourceParams, TibboDataType.TIME)})`;
                        break;
                    default:

                        break;
                }
                break;
            }
        }

        const source: BaseNode = project.getFeature(link.getSettingValue('SOURCE_FEATURE')) as BaseNode;

        const args = [];
        for (let j = 0; j < FIELDS.length; j += 1) {
            const field = FIELDS[j];
            let targetDataType: TibboDataType = TibboDataType.STRING;
            const fieldDataType = field.getSettingValue('FIELDTYPE');
            switch (fieldDataType) {
                case 'E':
                    targetDataType = TibboDataType.DATETIME;
                    break;
                case 'M':
                    targetDataType = TibboDataType.TIME;
                    break;
                case 'F':
                    targetDataType = TibboDataType.FLOAT;
                    break;
                default:
                    targetDataType = TibboDataType.STRING;
                    break;
            }
            const param = `${project.convertDataType(`tbl_field_get("${field.getSettingValue('NAME')}")`, TibboDataType.STRING, targetDataType)}`;
            args.push(param);
        }

        const wraparound = feature.getSettingValue('WRAPAROUND') === '1';
        let selectorCode = `tbl_record_find_sorted(EN_TBL_RECORD_ACTIVE,${fieldValue},"${FIELD_NAME}",table_find_result_index,${wraparound},${OPERATOR})`;
        if (OPERATOR === 'PL_FD_FIND_EQUAL') {
            selectorCode = `tbl_record_find_sorted(EN_TBL_RECORD_ACTIVE,${fieldValue},"${FIELD_NAME}",table_find_result_index,NO,${OPERATOR})`;
        }
        blocks.push({
            location: link.getSourceId(),
            blockType: BlockType.CODE,
            id: link.getID(),
            template: `
    tbl_select("${TABLE_NAME}","${TABLE_NAME}")
    tmp_table_find_result_index = ${find_index} + 1
    table_find_result_index = ${find_index} + 1
    ${selectorCode}
    if table_find_result_index<>0 then
        tbl_record_sg(table_find_result_index,EN_TBL_GET)
        ${findFunctionName}(table_find_result_index,${args.join(',')})
    else
        ${notFound}
        
    end if`,
        });
    }

    let foundCode = '';
    links = project.getNodeLinks(feature, 'tbl_record_found');
    if (links.length > 0) {
        const link = links[0];
        foundCode += `'****${link.getSourceId()}****'`;
    }

    blocks.push({
        location: 'DEVICE_TBS',
        id: `sub_${findFunctionName}`,
        blockType: BlockType.CODE,
        template: `
sub ${findFunctionName}(row_index as word,${params.join(',')})
    ${foundCode}
end sub`,
    });

    links = project.getNodeLinks(feature, 'tbl_add');
    for (let i = 0; i < links.length; i += 1) {
        const link = links[i];
        const FIELDS = TABLE.getSettingValue('FIELDS');
        let rowStr = '';
        for (let i = 0; i < FIELDS.length; i += 1) {
            const field = FIELDS[i];
            rowStr += `${project.getNodeParameter(link, field.getSettingValue('NAME'))}`;
            if (i < FIELDS.length - 1) {
                rowStr += '+","+';
            }
        }
        blocks.push({
            location: link.getSourceId(),
            blockType: BlockType.CODE,
            id: link.getID(),
            template: `
        tbl_web_add_row("${TABLE_NAME}",${rowStr})`,
        });
    }

    return blocks;
}
